import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  PropertyPermission,
  UserPermission,
} from 'src/modules/shared/types/permissions.type';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropertyOperation, type CatalogProperty } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useTenantCurrencyConfig } from 'src/modules/shared/hooks/api/currency';
import { PropertyEditorSectionCard } from '../molecules/property-editor-section-card';
import { PropertyEditorOverviewSection } from '../molecules/property-editor-overview-section';
import { PropertyEditorPricingSection } from '../molecules/property-editor-pricing-section';
import { PropertyEditorCharacteristicsSection } from '../molecules/property-editor-characteristics-section';
import { PropertyLocationFields } from '../molecules/property-location-fields';
import { PropertyMediaManager } from '../molecules/property-media-manager';
import { PropertyExternalMediaFields } from '../molecules/property-external-media-fields';
import {
  buildPropertyFormSchema,
  type PropertyFormInputValues,
  type PropertyFormValues,
} from '../../validations/property-validations';
import { useTenantCountryCode } from 'src/modules/shared/hooks/use-tenant-country-code';
import { getPropertyFormDefaults } from '../../utils/property-form.util';
import {
  buildPropertyEditorCurrencyOptions,
  getVisibleSurfaceFields,
  propertyEditorPropertyConditionOptions,
  propertyEditorOrientationOptions,
  propertyFeatureGroups,
  propertyEditorPropertyTypeOptions,
  shouldShowRentPricing,
  shouldShowSalePricing,
} from '../../utils/property-editor-form.util';

interface PropertyEditorFormProps {
  formId: string;
  property?: CatalogProperty | null;
  loading: boolean;
  onSubmit: (values: PropertyFormValues) => Promise<void>;
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Main property editor form used by the route-based property create/edit pages.
 */
export const PropertyEditorForm: React.FC<PropertyEditorFormProps> = ({
  formId,
  property,
  loading,
  onSubmit,
  onDirtyChange,
}) => {
  const { t } = useI18n();
  const tenantCountryCode = useTenantCountryCode();
  const { hasPermission: canCreate } = useHasPermissions(
    PropertyPermission.Create
  );
  const { hasPermission: canUpdateAll } = useHasPermissions(
    PropertyPermission.Update
  );
  const { hasPermission: canUpdateScoped } = useHasPermissions(
    PropertyPermission.UpdateScoped
  );
  const { hasPermission: canReadUsers } = useHasPermissions(
    UserPermission.Read
  );
  const { user } = useCurrentUser();
  const canEdit = property
    ? canUpdateAll ||
      (canUpdateScoped && user != null && property.userId === user.id)
    : canCreate;
  const canAssign = canUpdateAll && (property != null || canCreate);
  const { users: assignees } = useAllUsers(
    createQuery().equals('kind', 'person').build(),
    canReadUsers
  );
  const assignee = assignees.find(
    (candidate) => candidate.id === property?.userId
  );
  const propertyFormSchema = React.useMemo(
    () => buildPropertyFormSchema(t),
    [t]
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { dirtyFields, errors, isDirty, isSubmitting },
  } = useForm<PropertyFormInputValues, unknown, PropertyFormValues>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues: getPropertyFormDefaults(property),
  });

  const { config: currencyConfig, loading: currencyConfigLoading } =
    useTenantCurrencyConfig();
  const enabledCurrencies = React.useMemo(
    () => currencyConfig?.enabledCurrencies ?? [],
    [currencyConfig?.enabledCurrencies]
  );

  const [
    operation,
    salePriceCurrency,
    rentPriceCurrency,
    propertyType,
    countryCode,
    addressDisplay,
    streetName,
    streetNumber,
    unit,
    neighborhood,
    locality,
    region,
    latitude,
    longitude,
  ] = watch([
    'operation',
    'salePriceCurrency',
    'rentPriceCurrency',
    'propertyType',
    'countryCode',
    'addressDisplay',
    'streetName',
    'streetNumber',
    'unit',
    'neighborhood',
    'locality',
    'region',
    'latitude',
    'longitude',
  ]);

  const showSalePricing = shouldShowSalePricing(operation);
  const showRentPricing = shouldShowRentPricing(operation);
  const visibleSurfaceFields = React.useMemo(
    () => getVisibleSurfaceFields(propertyType),
    [propertyType]
  );
  const defaultCurrency = React.useMemo(
    () => currencyConfig?.baseCurrency ?? '',
    [currencyConfig?.baseCurrency]
  );
  const currencyOptions = React.useMemo(
    () =>
      buildPropertyEditorCurrencyOptions(enabledCurrencies, [
        property?.salePriceCurrency,
        property?.rentPriceCurrency,
        salePriceCurrency,
        rentPriceCurrency,
      ]),
    [
      enabledCurrencies,
      property?.salePriceCurrency,
      property?.rentPriceCurrency,
      salePriceCurrency,
      rentPriceCurrency,
    ]
  );

  useEffect(() => {
    reset(getPropertyFormDefaults(property));
  }, [property, reset]);

  useEffect(() => {
    if (
      !property &&
      tenantCountryCode &&
      !getValues('countryCode') &&
      !dirtyFields.countryCode
    ) {
      setValue('countryCode', tenantCountryCode, { shouldDirty: false });
    }
  }, [
    property,
    tenantCountryCode,
    getValues,
    setValue,
    dirtyFields.countryCode,
  ]);

  useEffect(() => {
    if (!defaultCurrency) {
      return;
    }

    if (!getValues('salePriceCurrency') && !dirtyFields.salePriceCurrency) {
      setValue('salePriceCurrency', defaultCurrency, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (!getValues('rentPriceCurrency') && !dirtyFields.rentPriceCurrency) {
      setValue('rentPriceCurrency', defaultCurrency, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [
    defaultCurrency,
    dirtyFields.rentPriceCurrency,
    dirtyFields.salePriceCurrency,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const operationOptions = React.useMemo(
    () => [
      {
        content: t('catalog.properties.operation.SALE'),
        value: PropertyOperation.SALE,
      },
      {
        content: t('catalog.properties.operation.RENT'),
        value: PropertyOperation.RENT,
      },
      {
        content: t('catalog.properties.operation.SALE_AND_RENT'),
        value: PropertyOperation.SALE_AND_RENT,
      },
      {
        content: t('catalog.properties.operation.TEMPORARY_RENT'),
        value: PropertyOperation.TEMPORARY_RENT,
      },
    ],
    [t]
  );

  const localizedPropertyTypeOptions = React.useMemo(
    () =>
      propertyEditorPropertyTypeOptions.map((option) =>
        option.labelKey
          ? {
              content: t(option.labelKey),
              value: option.value,
            }
          : { content: '—', value: option.value }
      ),
    [t]
  );

  const localizedPropertyConditionOptions = React.useMemo(
    () =>
      propertyEditorPropertyConditionOptions.map((option) => ({
        content: t(option.labelKey),
        value: option.value,
      })),
    [t]
  );

  const localizedOrientationOptions = React.useMemo(
    () =>
      propertyEditorOrientationOptions.map((option) =>
        option.labelKey
          ? {
              content: t(option.labelKey),
              value: option.value,
            }
          : { content: '—', value: option.value }
      ),
    [t]
  );

  const localizedFeatureOptions = React.useMemo(
    () =>
      propertyFeatureGroups.map((group) => ({
        title: t(group.titleKey),
        features: group.features.map((feature) => ({
          fieldName: feature.fieldName as keyof PropertyFormValues,
          label: t(feature.labelKey),
        })),
      })),
    [t]
  );

  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit({
      ...values,
      userId: canAssign ? values.userId : undefined,
    });
    reset(values);
  });

  const assigneeField =
    canAssign || property ? (
      <div className="flex flex-col gap-1">
        <Label size="md">{t('catalog.properties.table.assigned')}</Label>
        {canAssign ? (
          <Controller
            control={control}
            name="userId"
            render={({ field }) => (
              <Select
                aria-label={t('catalog.properties.table.assigned')}
                name={field.name}
                onBlur={field.onBlur}
                value={field.value ?? ''}
                options={[
                  {
                    value: '',
                    content: t('catalog.properties.assigned.unassigned'),
                  },
                  ...assignees.map((user) => ({
                    value: String(user.id),
                    content: formatFullName(
                      user.firstName,
                      user.lastName,
                      user.email
                    ),
                  })),
                ]}
                onChange={(event) =>
                  field.onChange(
                    event.target.value ? Number(event.target.value) : null
                  )
                }
              />
            )}
          />
        ) : (
          <Text className="min-h-[34px] py-1.5">
            {property?.userId == null
              ? t('catalog.properties.assigned.unassigned')
              : assignee
                ? formatFullName(
                    assignee.firstName,
                    assignee.lastName,
                    assignee.email
                  )
                : `#${property.userId}`}
          </Text>
        )}
      </div>
    ) : null;

  return (
    <form
      id={formId}
      className="flex flex-col gap-5"
      onSubmit={handleFormSubmit}
    >
      <fieldset
        disabled={loading || isSubmitting || !canEdit}
        className="contents disabled:cursor-not-allowed"
      >
        <PropertyEditorOverviewSection
          property={property}
          assigneeField={assigneeField}
          control={control}
          register={register}
          nameError={errors.name?.message}
          onNameChange={() => void trigger('name')}
          operationOptions={operationOptions}
          propertyTypeOptions={localizedPropertyTypeOptions}
          propertyConditionOptions={localizedPropertyConditionOptions}
          t={t}
        />

        <PropertyEditorPricingSection
          control={control}
          register={register}
          currencyOptions={currencyOptions}
          currencyOptionsLoading={currencyConfigLoading}
          showSalePricing={showSalePricing}
          showRentPricing={showRentPricing}
          t={t}
        />

        <PropertyLocationFields
          register={register}
          setValue={setValue}
          disabled={loading || isSubmitting || !canEdit}
          tenantCountryCode={tenantCountryCode}
          values={{
            countryCode,
            addressDisplay,
            streetName,
            streetNumber,
            unit,
            neighborhood,
            locality,
            region,
            latitude,
            longitude,
          }}
        />

        <PropertyEditorSectionCard
          title={t('catalog.properties.editor.sections.media')}
          description={t('catalog.properties.editor.sections.mediaDescription')}
        >
          <Controller
            control={control}
            name="media"
            render={({ field: { value, onChange } }) => (
              <PropertyMediaManager
                media={value ?? []}
                onChange={onChange}
                propertyId={property?.id}
                disabled={loading || isSubmitting || !canEdit}
                title={t('catalog.properties.media.title')}
                description={t('catalog.properties.media.description')}
                emptyLabel={t('catalog.properties.media.empty')}
              />
            )}
          />
          <PropertyExternalMediaFields
            control={control}
            errors={errors}
            loading={loading || isSubmitting || !canEdit}
            register={register}
            setValue={setValue}
          />
        </PropertyEditorSectionCard>

        <PropertyEditorCharacteristicsSection
          register={register}
          control={control}
          propertyType={propertyType}
          visibleSurfaceFields={visibleSurfaceFields}
          t={t}
          orientationOptions={localizedOrientationOptions}
          featureGroups={localizedFeatureOptions}
        />
        <button
          type="submit"
          className="hidden"
          disabled={loading || isSubmitting || !canEdit}
          aria-hidden="true"
        />
      </fieldset>
    </form>
  );
};
